import { useEffect, useState } from 'react';
import TaskCategoryService from '../../services/TaskCategoryService';
import TaskPlatformService from '../../services/TaskPlatformService';

/**
 * Per-video fields set once for every language: category, duration and the source video link.
 * Pure helpers plus the cached category and platform lists.
 */

/** "12:30" or "1:02:15" as seconds; null when empty or not a duration. Mirrors the backend VideoDuration. */
export const parseDuration = (text) => {
    const value = String(text ?? '').trim();
    if (!/^\d+(:\d{1,2}){1,2}$/.test(value)) return null;
    const parts = value.split(':').map(Number);
    if (parts.slice(1).some((n) => n > 59)) return null;
    const seconds = parts.reduce((total, n) => total * 60 + n, 0);
    return seconds > 0 ? seconds : null;
};

/** 750 as "12:30", 3735 as "1:02:15". */
export const formatDuration = (seconds) => {
    if (seconds == null || seconds <= 0) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** A full http(s) link with a host, like the backend accepts. */
export const isWebLink = (text) => {
    try {
        const url = new URL(String(text).trim());
        return (url.protocol === 'https:' || url.protocol === 'http:') && !!url.hostname;
    } catch (e) {
        return false;
    }
};

const MAX_SECONDS = 24 * 3600;

/** Why a typed duration can't be saved, or null (empty is fine). */
export const durationError = (duration) => {
    if (!(duration || '').trim()) return null;
    const seconds = parseDuration(duration);
    if (seconds == null) return 'Type it as minutes:seconds (12:30) or hours:minutes:seconds (1:02:15)';
    return seconds > MAX_SECONDS ? 'Duration must be under 24 hours' : null;
};

/** Why a typed source link can't be saved, or null (empty is fine). */
export const sourceUrlError = (sourceUrl) =>
    (sourceUrl || '').trim() && !isWebLink(sourceUrl) ? 'Paste the full link to the source video, starting with https://' : null;

/** Why the typed fields ({ category, duration, sourceUrl }) can't be saved, or null. */
export const contentFieldsError = ({ duration, sourceUrl } = {}) => durationError(duration) || sourceUrlError(sourceUrl);

/**
 * The languages to track after the file's own languages go from before to after. One added
 * to the file is tracked, one taken off it isn't. Others stay: one taken off the tracked list
 * stays off, and one tracked that the file doesn't have stays on.
 */
export const carryLanguages = (tracked, before, after) => [
    ...tracked.filter((l) => after.includes(l) || !before.includes(l)),
    ...after.filter((l) => !before.includes(l) && !tracked.includes(l)),
];

/**
 * The request's contentFields from the typed fields; undefined when nothing was filled in.
 * platform: with always, empty takes it off and undefined leaves it alone.
 */
export const contentFieldsFrom = ({ category, duration, sourceUrl, platform } = {}, { always = false } = {}) => {
    const body = {
        categoryKey: category || null,
        durationSeconds: parseDuration(duration),
        sourceUrl: (sourceUrl || '').trim() || null,
        platform: always && platform !== undefined ? platform || '' : platform || null,
    };
    if (!always && !body.categoryKey && body.durationSeconds == null && !body.sourceUrl && !body.platform) return undefined;
    return body;
};

/**
 * A list admins keep, loaded once per page and shared by everything showing it. initial shows
 * until the list loads, and stays where it can't load (a public link has no login).
 */
const cachedList = (fetchList, initial = null) => {
    let cache = initial;
    let loading = null;
    const listeners = new Set();

    const load = () => {
        if (!loading) {
            loading = fetchList()
                .then((list) => {
                    cache = list;
                    listeners.forEach((fn) => fn(list));
                    return list;
                })
                .catch(() => {
                    loading = null;
                    return cache || [];
                });
        }
        return loading;
    };

    const refresh = () => {
        loading = null;
        return load();
    };

    const useList = () => {
        const [list, setList] = useState(cache || []);
        useEffect(() => {
            listeners.add(setList);
            if (cache) setList(cache);
            load();
            return () => listeners.delete(setList);
        }, []);
        return list;
    };

    return [useList, refresh];
};

/** Every category, hidden ones too (old tasks still show their label). Refresh after an admin changes it. */
export const [useTaskCategories, refreshTaskCategories] = cachedList(() => TaskCategoryService.list());

/** Every platform, hidden ones too. Refresh after an admin changes it. */
export const [useTaskPlatforms, refreshTaskPlatforms] = cachedList(() => TaskPlatformService.list(), [
    { key: 'YOUTUBE', label: 'YouTube', order: 1 },
    { key: 'INSTAGRAM_FACEBOOK', label: 'Instagram/Facebook', order: 2 },
    { key: 'CENTERS_KIT', label: 'Centers kit', order: 3 },
]);

/** What can be picked: not the hidden ones, except the one already set, so the field isn't blank. */
export const platformChoices = (platforms, current) =>
    platforms.filter((p) => !p.hidden || p.key === current).map((p) => ({ value: p.key, label: p.label }));

export const categoryLabel = (categories, key) => (key ? categories.find((c) => c.key === key)?.label || key : '');

export const platformLabel = categoryLabel;
